import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePortal } from '@/contexts/PortalContext';
import { 
  ArrowLeft,
  Award, 
  User, 
  CheckCircle,
  Clock,
  AlertTriangle,
  Loader2,
  BookOpen,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  not_started: { label: '-', color: 'text-gray-400' },
  submitted: { label: 'Submitted', color: 'text-blue-600' },
  late: { label: 'Late', color: 'text-orange-600' },
  graded: { label: '', color: 'text-purple-600' },
  returned: { label: 'Returned', color: 'text-red-600' }
};

export default function InstructorGradebook() {
  const { token } = useAuth();
  const { isInstructor } = usePortal();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [gradebook, setGradebook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingGradebook, setLoadingGradebook] = useState(false);

  // Fetch courses
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(`${API_URL}/api/portal/courses?status=active`, {
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (response.ok) {
          const data = await response.json();
          setCourses(data.courses || []);
          
          // Auto-select first course
          if (data.courses.length > 0) {
            setSelectedCourse(data.courses[0].course_id);
          }
        }
      } catch (err) {
        console.error('Failed to fetch courses:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isInstructor) {
      fetchCourses();
    } else {
      setLoading(false);
    }
  }, [token, isInstructor]);

  // Fetch gradebook when course selected
  useEffect(() => {
    const fetchGradebook = async () => {
      if (!selectedCourse) return;
      
      setLoadingGradebook(true);
      try {
        const response = await fetch(
          `${API_URL}/api/portal/assignments/instructor/gradebook/${selectedCourse}`,
          {
            credentials: 'include',
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          }
        );
        
        if (response.ok) {
          setGradebook(await response.json());
        }
      } catch (err) {
        console.error('Failed to fetch gradebook:', err);
      } finally {
        setLoadingGradebook(false);
      }
    };

    fetchGradebook();
  }, [selectedCourse, token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getLetterGrade = (percentage) => {
    if (percentage >= 93) return 'A';
    if (percentage >= 90) return 'A-';
    if (percentage >= 87) return 'B+';
    if (percentage >= 83) return 'B';
    if (percentage >= 80) return 'B-';
    if (percentage >= 77) return 'C+';
    if (percentage >= 73) return 'C';
    if (percentage >= 70) return 'C-';
    if (percentage >= 67) return 'D+';
    if (percentage >= 63) return 'D';
    if (percentage >= 60) return 'D-';
    return 'F';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading gradebook...</p>
        </div>
      </div>
    );
  }

  if (!isInstructor) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-2">Instructor Access Required</h3>
          <p className="text-gray-500">This page is only accessible to instructors.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="instructor-gradebook-page">
      {/* Back Button */}
      <Link to="/portal/instructor/assignments" className="inline-flex items-center text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Assignments
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Gradebook</h1>
          <p className="text-gray-500 mt-1">View and manage student grades</p>
        </div>
        
        <Select value={selectedCourse || ''} onValueChange={setSelectedCourse}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Select a course" />
          </SelectTrigger>
          <SelectContent>
            {courses.map((course) => (
              <SelectItem key={course.course_id} value={course.course_id}>
                {course.code}: {course.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loadingGradebook ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" />
        </div>
      ) : !gradebook ? (
        <Card>
          <CardContent className="p-8 text-center">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Select a Course</h3>
            <p className="text-gray-500">Choose a course from the dropdown to view its gradebook.</p>
          </CardContent>
        </Card>
      ) : gradebook.assignments.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">No Assignments</h3>
            <p className="text-gray-500">This course doesn't have any assignments yet.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Course Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{gradebook.total_students}</div>
                <div className="text-sm text-gray-500">Students</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{gradebook.total_assignments}</div>
                <div className="text-sm text-gray-500">Assignments</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {gradebook.assignments.reduce((acc, a) => acc + a.points_possible, 0)}
                </div>
                <div className="text-sm text-gray-500">Total Points</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {gradebook.students.length > 0 
                    ? (gradebook.students.reduce((acc, s) => acc + s.overall_percentage, 0) / gradebook.students.length).toFixed(1)
                    : 0}%
                </div>
                <div className="text-sm text-gray-500">Class Average</div>
              </CardContent>
            </Card>
          </div>

          {/* Gradebook Table */}
          <Card>
            <CardHeader>
              <CardTitle>
                {gradebook.course_code}: {gradebook.course_title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium text-gray-900 sticky left-0 bg-gray-50 min-w-[200px]">
                        Student
                      </th>
                      {gradebook.assignments.map((assignment) => (
                        <th key={assignment.assignment_id} className="text-center p-3 font-medium text-gray-900 min-w-[100px]">
                          <div className="text-sm">{assignment.title.substring(0, 20)}...</div>
                          <div className="text-xs text-gray-500 font-normal">
                            {assignment.points_possible} pts
                          </div>
                        </th>
                      ))}
                      <th className="text-center p-3 font-medium text-gray-900 min-w-[100px] bg-purple-50">
                        Total
                      </th>
                      <th className="text-center p-3 font-medium text-gray-900 min-w-[80px] bg-purple-50">
                        Grade
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradebook.students.map((student) => (
                      <tr key={student.user_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 sticky left-0 bg-white">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{student.name}</div>
                              <div className="text-xs text-gray-500">{student.email}</div>
                            </div>
                          </div>
                        </td>
                        {student.assignments.map((grade) => {
                          const assignment = gradebook.assignments.find(a => a.assignment_id === grade.assignment_id);
                          const config = statusConfig[grade.status] || statusConfig.not_started;
                          
                          return (
                            <td key={grade.assignment_id} className="text-center p-3">
                              {grade.score !== null ? (
                                <div className={`font-medium ${config.color}`}>
                                  {grade.score}
                                  {!grade.is_published && (
                                    <span className="text-xs text-gray-400 ml-1">(draft)</span>
                                  )}
                                </div>
                              ) : (
                                <span className={`text-sm ${config.color}`}>
                                  {config.label}
                                </span>
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center p-3 bg-purple-50 font-medium">
                          {student.total_earned}/{student.total_possible}
                        </td>
                        <td className="text-center p-3 bg-purple-50">
                          <div className="font-bold text-purple-700">
                            {getLetterGrade(student.overall_percentage)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {student.overall_percentage.toFixed(1)}%
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
